import {Injectable} from '@angular/core';
import {BusinessPartner} from '../../model/business-partner';
import {HttpResponse, HttpStatusCode} from '@angular/common/http';
import {HttpClientService} from '../http-client/http-client.service';
import {EnvironmentService} from '../environment/environment.service';
import {BusinessPartnerDto, BusinessPartnerFilterProductResponseDto, BusinessPartnersDto} from './personal-dto';
import {catchError, map} from 'rxjs/operators';
import {Observable, of, ReplaySubject, shareReplay, switchMap, throwError} from 'rxjs';
import {DacklAlert, DacklAlertService} from '@datev/ngx-dackl';
import {TranslateService} from '@ngx-translate/core';
import {AdobeTrackingService} from '@datev/ngx-adobe-tracking';


@Injectable({
  providedIn: 'root'
})
export class PersonalService {

  constructor(private readonly httpClientService: HttpClientService,
              private readonly environmentService: EnvironmentService,
              private readonly alertService: DacklAlertService,
              private readonly translateService: TranslateService,
              private readonly adobeTrackingService: AdobeTrackingService) {
  }

  private readonly businessPartnersResource = this.environmentService.bePersonalUrl + 'businesspartners';

  private readonly activeBusinessPartner
    = new ReplaySubject<BusinessPartner>(1);

  private businessPartnerList: BusinessPartner[] = [];

  readonly activeBusinessPartner$ = this.activeBusinessPartner.asObservable();

  private latestFilterHKNRs?: [number, Observable<number[]>];

  public readonly invalidBusinessPartner: BusinessPartner = { businessPartnerId: 0, name: '', memberId: 0 };

  businessPartnersResourceTemplate = () : string => `${this.businessPartnersResource}`;
  businessPartnersFilterProductResourceTemplate = (businessPartnerId: number) : string => `${this.businessPartnersResource}/${businessPartnerId}/filterproducts`;

  getBusinessPartners(): Observable<BusinessPartner[]> {
    return this.httpClientService.get<BusinessPartnersDto>(this.businessPartnersResource)
      .pipe(
        map((resp: HttpResponse<BusinessPartnersDto>) =>
          this.mapBusinessPartnersResponse(resp.body as BusinessPartnersDto)
        ),
        catchError(err => {
          this.adobeTrackingService.publishClickEvent('Error:Personal:BusinessPartners');
          return this.notifyInformationMessage().pipe(
            switchMap(() => { throw err; })
          );
        }),
        shareReplay(1)
      );
  }

  refreshDefaultBusinessPartner(): void {
    if (this.businessPartnerList.length === 0) {
      this.getBusinessPartners().subscribe({
        next: bps => {
          this.businessPartnerList = [];
          let selected = false;
          bps.forEach(bp => {
            if (bp) {
              this.businessPartnerList.push(bp);
              if (!selected) {
                this.activeBusinessPartner.next(bp);
                selected = true;
              }
            }
          });
        },
        error:  () => this.activeBusinessPartner.next(this.invalidBusinessPartner)
      });
    }
  }

  setActiveBusinessPartner(businessPartnerId: number): void {
    const bps = this.businessPartnerList
      .filter((bp: BusinessPartner) => bp.businessPartnerId === businessPartnerId);
    if (bps && bps.length > 0) {
      this.activeBusinessPartner.next(bps[0]);
    }
  }

  private mapBusinessPartnersResponse(businessPartnersDto: BusinessPartnersDto): BusinessPartner[] {
    if (null === businessPartnersDto) {
      return new Object(null) as BusinessPartner[];
    }
    this.businessPartnerList = [];
    if (!!businessPartnersDto.businesspartners) {
      businessPartnersDto.businesspartners.forEach(bp => {
        const businesspartner = this.mapBusinessPartner(bp);
        if (businesspartner) {
          this.businessPartnerList.push(businesspartner);
        }
      });
    }
    return this.businessPartnerList;
  }

  private mapBusinessPartner(businessPartnerDto: BusinessPartnerDto): BusinessPartner | undefined {

    if (!businessPartnerDto?.businesspartnerId || (businessPartnerDto?.memberId == null)
      || !businessPartnerDto?.name) {
      return undefined;
    }
    return {
      businessPartnerId: businessPartnerDto.businesspartnerId,
      memberId: businessPartnerDto.memberId,
      name: businessPartnerDto.name
    };
  }

  getBusinessPartnerFilterHKNRs(businessPartnerId: number): Observable<number[]> {
    if (!this.latestFilterHKNRs || this.latestFilterHKNRs[0] !== businessPartnerId) {
      if (businessPartnerId <= 0) {
        return throwError(() => ({
          errorCode: HttpStatusCode.NotAcceptable,
          errorText: 'No valid business partner available.',
          errorLink: '',
          errorLinkText: ''
        }));
      }

      const filterHKNRsObservable = this.httpClientService.get<BusinessPartnerFilterProductResponseDto>(
        this.businessPartnersFilterProductResourceTemplate(businessPartnerId)).pipe(
          map((resp: HttpResponse<BusinessPartnerFilterProductResponseDto>) =>
            this.mapBusinessPartnerFilterProductResponse(resp.body as BusinessPartnerFilterProductResponseDto)),
          catchError(err => {
            this.adobeTrackingService.publishClickEvent('Error:Personal:FilterProduct');
            return this.notifyInformationMessage().pipe(
                switchMap(() => { throw err; })
            );
          }),
          shareReplay(2));
      this.latestFilterHKNRs = [businessPartnerId, filterHKNRsObservable];
    }
    return this.latestFilterHKNRs[1];
  }

  private mapBusinessPartnerFilterProductResponse(responseDto: BusinessPartnerFilterProductResponseDto): number[] {
    const filterProductHKNRs: number[] = [];
    if (responseDto?.filterproducts) {
      responseDto.filterproducts.forEach(filterProductDto => {
        filterProductHKNRs?.push(filterProductDto.hknr);
      });
    }
    return filterProductHKNRs;
  }

  notifyInformationMessage(): Observable<boolean> {
    return this.translateService.get(
      [ 'alerts.info_cannot_personalize_title', 'alerts.info_cannot_personalize_message'])
      .pipe(
        switchMap(translations => {
          this.alertService.registerWarning(
            new DacklAlert({
              title: translations['alerts.info_cannot_personalize_title'],
              message: translations['alerts.info_cannot_personalize_message'],

            }));
          return of(true);
        })
      );
  }

}
